import { type SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getRevertedStatusForTrip } from './getRevertedStatusForTrip';
import type {
  BulkTripRejectWrite,
  NewStatusEnum,
  PatchedTripWrite,
  TripAddTripLegWrite,
  TripsBulkCreateWrite,
} from 'src/common/external/bambi-api/tripsApi';
import {
  useLazyTripsRetrieveQuery,
  useTripsAcceptPartialUpdateMutation,
  useTripsActivateWillCallPartialUpdateMutation,
  useTripsAddTripLegPartialUpdateMutation,
  useTripsAssignPartialUpdateMutation,
  useTripsBulkRejectCreateMutation,
  useTripsCancelPartialUpdateMutation,
  useTripsCompletePartialUpdateMutation,
  useTripsCreateMutation,
  useTripsDestroyMutation,
  useTripsLockPartialUpdateMutation,
  useTripsOverrideStatusPartialUpdateMutation,
  useTripsPartialUpdateMutation,
  useTripsReassignPartialUpdateMutation,
  useTripsRejectPartialUpdateMutation,
  useTripsStatusPartialUpdateMutation,
  useTripsUnassignPartialUpdateMutation,
  useTripsUncancelPartialUpdateMutation,
  useTripsUnlockPartialUpdateMutation,
  useTripsUpdatePickupAtPartialUpdateMutation,
  useTripsValidatePartialUpdateMutation,
} from 'src/common/external/bambi-api/tripsApi';
import { selectDispatchQueryArgs } from 'src/features/dispatch/common/selectDispatchQueryArgs';
import type { RequestedTripIdsToReasonCodesMap } from 'src/features/dispatch/dispatcher.slice';
import { shouldSeparateRejectReasonValueAndLabel } from 'src/features/trip/utils';

export function useTripRepository() {
  // Assign and reassign answer with a dispatch delta merged straight into the
  // cached dispatch data, so they have to carry the filters that data was cut
  // with — otherwise a crew or trip the filters hid gets merged back in. Read
  // here rather than at each caller: one that forgot would silently get an
  // unfiltered delta.
  const dispatchQueryArgs = useSelector(selectDispatchQueryArgs);
  const [getTripImpl] = useLazyTripsRetrieveQuery();
  const [createTripImpl] = useTripsCreateMutation();
  const [updateTripImpl] = useTripsPartialUpdateMutation();
  const [destroyTripImpl] = useTripsDestroyMutation();
  const [assignImpl] = useTripsAssignPartialUpdateMutation();
  const [reassignImpl] = useTripsReassignPartialUpdateMutation();
  const [unassignImpl] = useTripsUnassignPartialUpdateMutation();
  const [cancelImpl] = useTripsCancelPartialUpdateMutation();
  const [uncancelImpl] = useTripsUncancelPartialUpdateMutation();
  const [completeImpl] = useTripsCompletePartialUpdateMutation();
  const [lockImpl] = useTripsLockPartialUpdateMutation();
  const [unlockImpl] = useTripsUnlockPartialUpdateMutation();
  const [statusImpl] = useTripsStatusPartialUpdateMutation();
  const [activateWillCallImpl] =
    useTripsActivateWillCallPartialUpdateMutation();
  const [updatePickupAtImpl] = useTripsUpdatePickupAtPartialUpdateMutation();
  const [validateTripAssignmentImpl] = useTripsValidatePartialUpdateMutation();
  const [overrideTripStatusImpl] =
    useTripsOverrideStatusPartialUpdateMutation();
  const [bulkRejectImpl] = useTripsBulkRejectCreateMutation();
  const [rejectImpl] = useTripsRejectPartialUpdateMutation();
  const [acceptImpl] = useTripsAcceptPartialUpdateMutation();
  const [addTripLegImpl] = useTripsAddTripLegPartialUpdateMutation();

  const repository = useMemo(() => {
    // Spread rather than listed out: a filter added to the board query has to
    // reach the delta too, and naming them here is what let the unassigned
    // panel's filters go missing. Ordering is the one arg the delta has no use
    // for.
    const { unassignedOrdering: _ordering, ...dispatchDeltaFilters } =
      dispatchQueryArgs;
    return {
      getTrip: async (tripId: string) => {
        return getTripImpl({
          id: tripId,
        }).unwrap();
      },
      createTrip: async (tripsBulkCreate: TripsBulkCreateWrite) => {
        return createTripImpl({
          tripsBulkCreate,
        }).unwrap();
      },
      updateTrip: async (tripId: string, patchedTrip: PatchedTripWrite) => {
        return updateTripImpl({
          id: tripId,
          patchedTrip,
        }).unwrap();
      },
      destroyTrip: async (tripId: string) => {
        return destroyTripImpl({
          id: tripId,
        }).unwrap();
      },
      validateAssignment: async (tripId: string, assignmentId: string) => {
        return validateTripAssignmentImpl({
          id: tripId,
          patchedTripValidate: {
            assignment_id: assignmentId,
          },
        }).unwrap();
      },
      assign: async (tripId: string, assignmentId: string) => {
        return assignImpl({
          id: tripId,
          patchedTripAssign: {
            assignment_id: assignmentId,
          },
          ...dispatchDeltaFilters,
        }).unwrap();
      },
      reassign: async (tripId: string, assignmentId: string) => {
        return reassignImpl({
          id: tripId,
          patchedTripReassign: {
            assignment_id: assignmentId,
          },
          ...dispatchDeltaFilters,
        }).unwrap();
      },
      unassign: async (tripId: string) => {
        return unassignImpl({
          id: tripId,
        }).unwrap();
      },
      cancel: async (tripId: string, reason?: string, code?: number) => {
        return cancelImpl({
          id: tripId,
          patchedTripCancel: {
            canceled_reason: reason,
            canceled_reason_code: code,
          },
        }).unwrap();
      },
      uncancel: async (tripId: string) => {
        return uncancelImpl({
          id: tripId,
          patchedTripUncancel: {
            // Mostly a mobile app concept so including this for type
            concurrent_trips: [],
          },
        }).unwrap();
      },
      complete: async (tripId: string) => {
        return completeImpl({
          id: tripId,
          patchedTripComplete: {
            // Mostly a mobile app concept so including this for type
            concurrent_trips: [],
            override: false,
          },
        }).unwrap();
      },
      completeWithNoValidations: async (tripId: string) => {
        return completeImpl({
          id: tripId,
          patchedTripComplete: {
            // Mostly a mobile app concept so including this for type
            concurrent_trips: [],
            override: true,
          },
        }).unwrap();
      },
      lock: async (tripId: string) => {
        return lockImpl({
          id: tripId,
        }).unwrap();
      },
      unlock: async (tripId: string) => {
        return unlockImpl({
          id: tripId,
        }).unwrap();
      },
      status: async (tripId: string, new_status: NewStatusEnum) => {
        return statusImpl({
          id: tripId,
          patchedTripStatusChange: {
            new_status,
          },
        }).unwrap();
      },
      activateWillCall: async (tripId: string, pickupTime: string) => {
        return activateWillCallImpl({
          id: tripId,
          patchedTripActivateWillCall: {
            pickup_at: pickupTime,
          },
        }).unwrap();
      },
      updatePickupTime: async (tripId: string, newTime: string) => {
        return updatePickupAtImpl({
          id: tripId,
          patchedTripUpdatePickupAt: {
            pickup_at: newTime,
          },
        }).unwrap();
      },
      revertStatus: async (tripId: string) => {
        const trip = await getTripImpl({
          id: tripId,
        }).unwrap();
        const status = trip.status;
        const newStatus: NewStatusEnum = getRevertedStatusForTrip(status);
        return statusImpl({
          id: tripId,
          patchedTripStatusChange: {
            new_status: newStatus,
          },
        }).unwrap();
      },
      overrideStatus: async (tripId: string, newStatus: NewStatusEnum) => {
        return overrideTripStatusImpl({
          id: tripId,
          patchedTripOverrideStatusChange: {
            override_status: newStatus,
          },
        }).unwrap();
      },
      // TODO: This really shouldn't know about RequestedTripIdsToReasonCodesMap
      bulkReject: async (
        requestedTripIdsToRejectToReasonCodes: RequestedTripIdsToReasonCodesMap
      ) => {
        const bulkTripReject: BulkTripRejectWrite = {
          trip_reasons: Object.keys(requestedTripIdsToRejectToReasonCodes).map(
            (tripId) => {
              return {
                trip_id: tripId,
                rejected_reason:
                  requestedTripIdsToRejectToReasonCodes[tripId]?.value ||
                  'other',
              };
            }
          ),
        };

        return bulkRejectImpl({
          bulkTripReject,
        }).unwrap();
      },
      reject: async (tripId: string, option: SelectOption | null) => {
        let parsedReasonCode: number | undefined = parseInt(
          `${option?.value}`,
          10
        );
        if (!Number.isInteger(parsedReasonCode)) {
          parsedReasonCode = undefined;
        }

        const trip = await getTripImpl(
          {
            id: tripId,
          },
          true
        ).unwrap();

        const separateLabelAndValue =
          shouldSeparateRejectReasonValueAndLabel(trip);
        const reasonLabel = separateLabelAndValue
          ? option?.label || 'other'
          : option?.value || 'other';
        const reasonCode = parsedReasonCode;

        return rejectImpl({
          id: tripId,
          patchedTripReject: {
            rejected_reason: reasonLabel,
            rejected_reason_code: reasonCode,
          },
        }).unwrap();
      },
      accept: async (tripId: string) => {
        return acceptImpl({
          id: tripId,
          // Not optional, so pass empty
          // Override param is not actually used and will probably break if passed
          // to api.
          patchedTripAccept: {},
        }).unwrap();
      },
      addTripLeg: async (
        tripId: string,
        tripAddTripLeg: TripAddTripLegWrite
      ) => {
        return addTripLegImpl({
          id: tripId,
          patchedTripAddTripLeg: tripAddTripLeg,
        }).unwrap();
      },
    };
  }, [
    acceptImpl,
    addTripLegImpl,
    activateWillCallImpl,
    assignImpl,
    bulkRejectImpl,
    cancelImpl,
    completeImpl,
    createTripImpl,
    destroyTripImpl,
    getTripImpl,
    lockImpl,
    overrideTripStatusImpl,
    dispatchQueryArgs,
    reassignImpl,
    rejectImpl,
    statusImpl,
    unassignImpl,
    uncancelImpl,
    unlockImpl,
    updatePickupAtImpl,
    updateTripImpl,
    validateTripAssignmentImpl,
  ]);
  return repository;
}
